import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { useLanguage } from '../../contexts/LanguageContext';
import { Organization, OrganizationUser } from '../../types/organization';
import { User, UserModulePermissions, ModulePermissions } from '../../types/user';
import { db } from '../../lib/firebase';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { emailService } from '../../services/emailService';
import { 
  UserGroupIcon, 
  BuildingOfficeIcon,
  UserIcon,
  PlusIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  LockClosedIcon,
  ArrowPathIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { DEFAULT_PERMISSIONS, MODULE_LABELS, PERMISSION_COLORS, PERMISSION_LABELS, MODULE_CATEGORIES } from '../../config/permissions';

interface SimpleUserManagerProps {
  onOrganizationUpdate?: (org: Organization) => void;
}

const SimpleUserManager: React.FC<SimpleUserManagerProps> = ({
  onOrganizationUpdate
}) => {
  const { user } = useAuth();
  const { organizations, loading: orgsLoading, refetch: refreshOrganizations } = useOrganizations();
  const { createNotification } = useNotifications();
  const { t } = useLanguage();
  
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [detailedUsers, setDetailedUsers] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);

  const [loading, setLoading] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingPermissions, setEditingPermissions] = useState<UserModulePermissions | null>(null);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [userListSearchTerm, setUserListSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'super_admin' | 'admin' | 'user'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Új felhasználó meghívás email alapján
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'user'>('user');
  const [inviting, setInviting] = useState(false);

  // Szerepkör módosítás
  const [userToChangeRole, setUserToChangeRole] = useState<User | null>(null);
  const [newRole, setNewRole] = useState<'admin' | 'user'>('user');

  // Kijelölt felhasználók (bulk műveletek)
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Felhasználó hozzáadása szerepkörrel
  const [userToAddWithRole, setUserToAddWithRole] = useState<User | null>(null);
  const [addRole, setAddRole] = useState<'admin' | 'user'>('user');

  // Felhasználók betöltése
  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as User[];
      setAllUsers(users);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Hiba a felhasználók betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  // Szervezeti felhasználók részletes adatainak betöltése
  const loadDetailedUsers = useCallback(async (orgUsers: OrganizationUser[]) => {
    try {
      const userPromises = orgUsers.map(async (orgUser) => {
        const userDoc = allUsers.find(u => u.id === orgUser.userId);
        return userDoc ? { ...userDoc, orgRole: orgUser.role } : null;
      });
      
      const users = await Promise.all(userPromises);
      const filteredUsers = users.filter(Boolean) as User[];
      
      setDetailedUsers(filteredUsers);
    } catch (error) {
      console.error('Error loading detailed users:', error);
      setDetailedUsers([]);
    }
  }, [allUsers]);

  // Elérhető felhasználók kiszámítása
  const calculateAvailableUsers = useCallback(() => {
    if (!selectedOrganization) return;
    
    const orgUserIds = selectedOrganization.users?.map(u => u.userId) || [];
    
    const available = allUsers.filter(listedUser => {
      // Ha már a szervezetben van, ne jelenjen meg
      if (orgUserIds.includes(listedUser.id)) return false;
      
      // Ha a jelenlegi felhasználó super_admin, akkor minden felhasználó hozzáadható
      if (user?.role === 'super_admin') return true;
      
      // Egyébként ne jelenjenek meg a super_admin felhasználók
      return listedUser.role !== 'super_admin';
    });
    
    setAvailableUsers(available);
  }, [selectedOrganization, allUsers, user?.role]);

  // Alapértelmezett jogosultságok lekérése
  const getDefaultPermissions = (role: string): UserModulePermissions => {
    return DEFAULT_PERMISSIONS[role] || DEFAULT_PERMISSIONS.user;
  };

  // Kiválasztott szervezet frissítése
  const refreshCurrentOrganization = async () => {
    if (!selectedOrganization) return;
    
    try {
      await refreshOrganizations();
      await loadUsers();
    } catch (error) {
      console.error('Error refreshing current organization:', error);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedOrganization && allUsers.length > 0) {
      loadDetailedUsers(selectedOrganization.users || []);
      calculateAvailableUsers();
    }
  }, [selectedOrganization, allUsers, loadDetailedUsers, calculateAvailableUsers]);

  // Frissítjük a kiválasztott szervezetet, ha a szervezetek listája változik
  useEffect(() => {
    if (selectedOrganization && organizations.length > 0) {
      const updatedOrg = organizations.find(org => org.id === selectedOrganization.id);
      if (updatedOrg && updatedOrg.users?.length !== selectedOrganization.users?.length) {
        setSelectedOrganization(updatedOrg);
      }
    }
  }, [organizations, selectedOrganization]);

  // Dropdown bezárása, ha valahol máshova kattintanak
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserDropdown]);

  // Szűrt felhasználók a keresés alapján
  const filteredAvailableUsers = availableUsers.filter(user => {
    // Kizárjuk a már hozzáadott felhasználókat
    const isAlreadyAdded = selectedOrganization?.users?.some(u => u.userId === user.id);
    if (isAlreadyAdded) return false;
    
    // Szűrés név és email alapján
    return user.displayName.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
           user.email.toLowerCase().includes(userSearchTerm.toLowerCase());
  });

  // Szűrt szervezetek
  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(orgSearchTerm.toLowerCase())
  );

  // Szűrt és lapozott felhasználók
  const filteredUsers = useMemo(() => {
    let filtered = detailedUsers;

    // Deduplikáció user.id alapján (dupla kulcs hiba javítása)
    const seen = new Set<string>();
    filtered = filtered.filter(u => {
      if (seen.has(u.id)) return false;
      seen.add(u.id);
      return true;
    });

    // Keresés
    if (userListSearchTerm) {
      filtered = filtered.filter(user =>
        user.displayName.toLowerCase().includes(userListSearchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(userListSearchTerm.toLowerCase())
      );
    }

    // Szerep szűrés
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    return filtered;
  }, [detailedUsers, userListSearchTerm, roleFilter]);

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredUsers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  // Reset oldal keresés/szűrés változásakor
  useEffect(() => {
    setCurrentPage(1);
  }, [userListSearchTerm, roleFilter]);

  // Felhasználó hozzáadása szervezethez
  const handleAddUser = async (userToAdd: User, role?: 'admin' | 'user') => {
    if (!selectedOrganization) return;

    // Ellenőrizzük, hogy már nincs-e hozzáadva
    const isAlreadyAdded = selectedOrganization.users?.some(u => u.userId === userToAdd.id);
    if (isAlreadyAdded) {
      toast.error('Ez a felhasználó már hozzá van adva ehhez a szervezethez');
      return;
    }

    const finalRole = role || (userToAdd.role === 'super_admin' ? 'admin' : userToAdd.role as 'admin' | 'user');

    const newOrgUser: OrganizationUser = {
      userId: userToAdd.id,
      email: userToAdd.email,
      role: finalRole,
      addedAt: new Date()
    };

    try {
      const orgRef = doc(db, 'organizations', selectedOrganization.id);
      await updateDoc(orgRef, {
        users: arrayUnion(newOrgUser),
        updatedAt: new Date()
      });

      // Felhasználó organizationId frissítése
      const userRef = doc(db, 'users', userToAdd.id);
      await updateDoc(userRef, {
        organizationId: selectedOrganization.id,
        updatedAt: new Date()
      });

      // Meghívó email küldése
      try {
        await emailService.sendUserInvitation({
          userEmail: userToAdd.email,
          userName: userToAdd.displayName || userToAdd.email,
          organizationName: selectedOrganization.name || 'YouCRM',
          userRole: finalRole,
          organizationId: selectedOrganization.id
        });
        
        toast.success('Felhasználó hozzáadva és meghívó email elküldve');
      } catch (emailError) {
        console.error('Error sending invitation email:', emailError);
        toast.success('Felhasználó hozzáadva, de az email küldés sikertelen volt');
      }

      // Értesítés létrehozása a hozzáadott felhasználónak
      try {
        const lang = t('common.language') === 'English' ? 'en' : 'hu';
        const translations = lang === 'en' 
          ? (await import('../../translations/en')).default.notifications
          : (await import('../../translations/hu')).default.notifications;
          
        await createNotification({
          type: 'system',
          title: translations.subjects?.userAdded || (lang === 'hu' ? 'Hozzáadtak egy szervezethez' : 'Added to an organization'),
          message: lang === 'hu'
            ? `${user?.displayName || 'Egy adminisztrátor'} hozzáadott a(z) ${selectedOrganization.name} szervezethez ${newOrgUser.role} szerepkörben.`
            : `${user?.displayName || 'An administrator'} added you to the ${selectedOrganization.name} organization as ${newOrgUser.role}.`,
          priority: 'medium',
          userId: userToAdd.id,
          organizationId: selectedOrganization.id,
          createdBy: user?.id,
          createdByName: user?.displayName,
          actionUrl: '/settings'
        });
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }

      // Frissítjük az adatokat a szerver oldalról is
      await refreshCurrentOrganization();
      
      setUserSearchTerm('');
      setShowUserDropdown(false);
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error('Hiba a felhasználó hozzáadásakor');
      
      // Ha hiba történt, visszaállítjuk az eredeti állapotot
      await refreshCurrentOrganization();
    }
  };

  // Felhasználó eltávolítása modal megnyitása
  const handleRemoveUser = (userToRemove: User) => {
    setUserToDelete(userToRemove);
  };

  // Felhasználó eltávolításának megerősítése
  const confirmRemoveUser = async () => {
    if (!selectedOrganization || !userToDelete) return;

    const orgUser = selectedOrganization.users?.find(u => u.userId === userToDelete.id);
    if (!orgUser) return;

    try {
      const orgRef = doc(db, 'organizations', selectedOrganization.id);
      await updateDoc(orgRef, {
        users: arrayRemove(orgUser),
        updatedAt: new Date()
      });

      // Felhasználó organizationId törlése
      const userRef = doc(db, 'users', userToDelete.id);
      await updateDoc(userRef, {
        organizationId: '',
        updatedAt: new Date()
      });

      // Értesítés létrehozása az eltávolított felhasználónak
      try {
        const lang = t('common.language') === 'English' ? 'en' : 'hu';
        const translations = lang === 'en' 
          ? (await import('../../translations/en')).default.notifications
          : (await import('../../translations/hu')).default.notifications;
          
        await createNotification({
          type: 'system',
          title: translations.subjects?.userRemoved || (lang === 'hu' ? 'Eltávolítottak egy szervezetből' : 'Removed from an organization'),
          message: lang === 'hu'
            ? `${user?.displayName || 'Egy adminisztrátor'} eltávolított a(z) ${selectedOrganization.name} szervezetből.`
            : `${user?.displayName || 'An administrator'} removed you from the ${selectedOrganization.name} organization.`,
          priority: 'high',
          userId: userToDelete.id,
          organizationId: selectedOrganization.id,
          createdBy: user?.id,
          createdByName: user?.displayName
        });
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }

      // Frissítjük az adatokat a szerver oldalról is
      await refreshCurrentOrganization();
      
      toast.success('Felhasználó eltávolítva');
      setUserToDelete(null);
    } catch (error) {
      console.error('Error removing user:', error);
      toast.error('Hiba a felhasználó eltávolításakor');
      
      // Ha hiba történt, visszaállítjuk az eredeti állapotot
      await refreshCurrentOrganization();
    }
  };

  // Jogosultságok kezelése
  const handleManagePermissions = (userToEdit: User) => {
    setSelectedUser(userToEdit);
    setEditingPermissions(userToEdit.permissions || getDefaultPermissions(userToEdit.role));
    setShowPermissions(true);
  };

  // Jogosultság váltása (toggle admin/access/none)
  const togglePermission = (module: keyof UserModulePermissions) => {
    if (!editingPermissions) return;
    
    const current = editingPermissions[module];
    let next: ModulePermissions;
    
    // Egyszerű ciklus: none -> access -> admin -> none
    switch (current) {
      case 'none': next = 'access'; break;
      case 'access': next = 'admin'; break;
      case 'admin': next = 'none'; break;
      default: next = 'none';
    }
    
    setEditingPermissions({
      ...editingPermissions,
      [module]: next
    });
  };

  // Gyors jogosultság beállítások
  const setAllPermissions = (permission: ModulePermissions) => {
    if (!editingPermissions) return;
    
    const newPermissions = { ...editingPermissions };
    Object.keys(MODULE_LABELS).forEach(moduleKey => {
      newPermissions[moduleKey as keyof UserModulePermissions] = permission;
    });
    
    setEditingPermissions(newPermissions);
  };

  const setDefaultPermissions = () => {
    if (!selectedUser) return;
    
    const defaultPerms = getDefaultPermissions(selectedUser.role);
    setEditingPermissions(defaultPerms);
  };

  // Jogosultságok mentése
  const handleSavePermissions = async () => {
    if (!selectedUser || !editingPermissions) return;

    setSavingPermissions(true);
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      await updateDoc(userRef, {
        permissions: editingPermissions,
        updatedAt: new Date()
      });

      // Értesítés létrehozása a felhasználónak
      try {
        const lang = t('common.language') === 'English' ? 'en' : 'hu';
        const translations = lang === 'en' 
          ? (await import('../../translations/en')).default.notifications
          : (await import('../../translations/hu')).default.notifications;
          
        await createNotification({
          type: 'system',
          title: translations.subjects?.permissionsChanged || (lang === 'hu' ? 'Jogosultságaid megváltoztak' : 'Your permissions have changed'),
          message: lang === 'hu'
            ? `${user?.displayName || 'Egy adminisztrátor'} módosította a jogosultságaidat a(z) ${selectedOrganization?.name} szervezetben.`
            : `${user?.displayName || 'An administrator'} modified your permissions in the ${selectedOrganization?.name} organization.`,
          priority: 'medium',
          userId: selectedUser.id,
          organizationId: selectedOrganization?.id || '',
          createdBy: user?.id,
          createdByName: user?.displayName,
          actionUrl: '/settings'
        });
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }
      
      await refreshCurrentOrganization();
      toast.success('Jogosultságok mentve');
      setShowPermissions(false);
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error('Hiba a jogosultságok mentésekor');
    } finally {
      setSavingPermissions(false);
    }
  };

  // Jogosultság színe
  const getPermissionColor = (permission: ModulePermissions) => {
    return PERMISSION_COLORS[permission] || PERMISSION_COLORS.none;
  };

  // Email alapú felhasználó meghívás
  const handleInviteUser = async () => {
    if (!selectedOrganization || !inviteEmail.trim()) return;

    setInviting(true);
    try {
      // Ellenőrizzük, hogy már létezik-e ez az email
      const existingUser = allUsers.find(u => u.email.toLowerCase() === inviteEmail.toLowerCase().trim());
      
      if (existingUser) {
        // Ha már létezik, adjuk hozzá a szervezethez
        await handleAddUser(existingUser);
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteName('');
        return;
      }

      // Új felhasználó meghívása email alapján
      await emailService.sendUserInvitation({
        userEmail: inviteEmail.trim(),
        userName: inviteName.trim() || inviteEmail.trim(),
        organizationName: selectedOrganization.name || 'YouCRM',
        userRole: inviteRole,
        organizationId: selectedOrganization.id
      });

      // Értesítés a meghívásról
      try {
        const lang = t('common.language') === 'English' ? 'en' : 'hu';
        const translations = lang === 'en' 
          ? (await import('../../translations/en')).default.notifications
          : (await import('../../translations/hu')).default.notifications;
          
        await createNotification({
          type: 'system',
          title: lang === 'hu' ? 'Felhasználó meghívva' : 'User invited',
          message: lang === 'hu'
            ? `${inviteName || inviteEmail} meghívva a(z) ${selectedOrganization.name} szervezetbe ${inviteRole} szerepkörrel.`
            : `${inviteName || inviteEmail} invited to ${selectedOrganization.name} as ${inviteRole}.`,
          priority: 'medium',
          userId: user?.id || '',
          organizationId: selectedOrganization.id,
          createdBy: user?.id || '',
          createdByName: user?.displayName || ''
        });
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }

      toast.success(`Meghívó email elküldve: ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('user');
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error('Hiba a meghívó küldésekor');
    } finally {
      setInviting(false);
    }
  };

  // Szerepkör módosítás
  const handleRoleChange = async () => {
    if (!userToChangeRole || !selectedOrganization) return;

    try {
      // Frissítjük a szervezet users tömbjében a szerepkört
      const oldOrgUser = selectedOrganization.users?.find(u => u.userId === userToChangeRole.id);
      if (!oldOrgUser) return;

      const newOrgUser = { ...oldOrgUser, role: newRole };

      const orgRef = doc(db, 'organizations', selectedOrganization.id);
      await updateDoc(orgRef, {
        users: arrayRemove(oldOrgUser),
        updatedAt: new Date()
      });
      await updateDoc(orgRef, {
        users: arrayUnion(newOrgUser),
        updatedAt: new Date()
      });

      // Frissítjük a felhasználó role-ját is
      const userRef = doc(db, 'users', userToChangeRole.id);
      await updateDoc(userRef, {
        role: newRole,
        updatedAt: new Date()
      });

      // Értesítés
      try {
        const lang = t('common.language') === 'English' ? 'en' : 'hu';
        const translations = lang === 'en' 
          ? (await import('../../translations/en')).default.notifications
          : (await import('../../translations/hu')).default.notifications;
          
        await createNotification({
          type: 'system',
          title: translations.subjects?.roleChanged || (lang === 'hu' ? 'Szerepkör módosítva' : 'Role changed'),
          message: lang === 'hu'
            ? `Szerepköröd megváltoztatva: ${newRole === 'admin' ? 'Adminisztrátor' : 'Felhasználó'} a(z) ${selectedOrganization.name} szervezetben.`
            : `Your role changed to ${newRole} in ${selectedOrganization.name}.`,
          priority: 'high',
          userId: userToChangeRole.id,
          organizationId: selectedOrganization.id,
          createdBy: user?.id,
          createdByName: user?.displayName,
          actionUrl: '/settings'
        });
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }

      await refreshCurrentOrganization();
      toast.success('Szerepkör módosítva');
      setUserToChangeRole(null);
    } catch (error) {
      console.error('Error changing role:', error);
      toast.error('Hiba a szerepkör módosításakor');
    }
  };

  // Felhasználó aktiválás/deaktiválás
  const handleToggleActive = async (userToToggle: User) => {
    try {
      const isCurrentlyActive = userToToggle.disabled !== true;
      const userRef = doc(db, 'users', userToToggle.id);
      await updateDoc(userRef, {
        disabled: isCurrentlyActive,
        updatedAt: new Date()
      });

      await refreshCurrentOrganization();
      toast.success(isCurrentlyActive ? 'Felhasználó deaktiválva' : 'Felhasználó aktiválva');
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Hiba a státusz módosításakor');
    }
  };

  // Bulk kijelölés váltása
  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size === paginatedUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(paginatedUsers.map(u => u.id)));
    }
  };

  // Bulk eltávolítás
  const handleBulkRemove = async () => {
    if (!selectedOrganization || selectedUserIds.size === 0) return;

    try {
      for (const userId of Array.from(selectedUserIds)) {
        const orgUser = selectedOrganization.users?.find(u => u.userId === userId);
        if (orgUser) {
          const orgRef = doc(db, 'organizations', selectedOrganization.id);
          await updateDoc(orgRef, {
            users: arrayRemove(orgUser),
            updatedAt: new Date()
          });

          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, {
            organizationId: '',
            updatedAt: new Date()
          });
        }
      }

      await refreshCurrentOrganization();
      toast.success(`${selectedUserIds.size} felhasználó eltávolítva`);
      setSelectedUserIds(new Set());
      setShowBulkActions(false);
    } catch (error) {
      console.error('Error bulk removing users:', error);
      toast.error('Hiba a tömeges eltávolításkor');
    }
  };

  // Bulk szerepkör módosítás
  const handleBulkRoleChange = async (role: 'admin' | 'user') => {
    if (!selectedOrganization || selectedUserIds.size === 0) return;

    try {
      for (const userId of Array.from(selectedUserIds)) {
        const oldOrgUser = selectedOrganization.users?.find(u => u.userId === userId);
        if (oldOrgUser && oldOrgUser.role !== role) {
          const newOrgUser = { ...oldOrgUser, role };
          const orgRef = doc(db, 'organizations', selectedOrganization.id);
          await updateDoc(orgRef, {
            users: arrayRemove(oldOrgUser),
            updatedAt: new Date()
          });
          await updateDoc(orgRef, {
            users: arrayUnion(newOrgUser),
            updatedAt: new Date()
          });

          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, {
            role,
            updatedAt: new Date()
          });
        }
      }

      await refreshCurrentOrganization();
      toast.success(`${selectedUserIds.size} felhasználó szerepköre módosítva: ${role}`);
      setSelectedUserIds(new Set());
      setShowBulkActions(false);
    } catch (error) {
      console.error('Error bulk changing roles:', error);
      toast.error('Hiba a tömeges szerepkör módosításkor');
    }
  };

  const getPermissionLabel = (permission: ModulePermissions) => {
    return PERMISSION_LABELS[permission] || PERMISSION_LABELS.none;
  };

  const modules: Array<{ key: keyof UserModulePermissions; label: string }> = 
    Object.entries(MODULE_LABELS).map(([key, label]) => ({ 
      key: key as keyof UserModulePermissions, 
      label 
    }));

  if (orgsLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">Betöltés...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Szervezet választó dropdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Szervezet kiválasztása
        </label>
        <div className="relative">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Keress szervezetet név alapján..."
              value={selectedOrganization ? selectedOrganization.name : orgSearchTerm}
              onChange={(e) => {
                setOrgSearchTerm(e.target.value);
                setShowOrgDropdown(true);
                if (!e.target.value) setSelectedOrganization(null);
              }}
              onFocus={() => setShowOrgDropdown(true)}
              className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                showOrgDropdown ? 'rotate-180' : ''
              }`} />
            </div>
          </div>

          {/* Dropdown lista */}
          {showOrgDropdown && (
            <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto">
              {filteredOrganizations.length > 0 ? (
                <div className="py-1">
                  {filteredOrganizations.map(org => (
                    <button
                      key={org.id}
                      onClick={() => {
                        setSelectedOrganization(org);
                        setOrgSearchTerm('');
                        setShowOrgDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-primary-50 dark:hover:bg-primary-900/20 flex items-center space-x-3 transition-colors ${
                        selectedOrganization?.id === org.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold">
                        {org.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {org.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {org.users?.length || 0} felhasználó
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  Nincs találat
                </div>
              )}
            </div>
          )}
        </div>
        
        {selectedOrganization && (
          <div className="mt-3 flex items-center justify-between p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-700">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold text-sm">
                {selectedOrganization.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {selectedOrganization.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedOrganization.users?.length || 0} felhasználó
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedOrganization(null);
                setOrgSearchTerm('');
              }}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {selectedOrganization && (
        <div className="space-y-6">
          {/* Felhasználó hozzáadása */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <PlusIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">Felhasználó hozzáadása</h4>
              </div>
              <button
                onClick={() => setShowInviteModal(true)}
                className="inline-flex items-center space-x-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
              >
                <EnvelopeIcon className="w-5 h-5" />
                <span>Felhasználó meghívása</span>
              </button>
            </div>

            {/* Meglévő felhasználó hozzáadása (collapsible) */}
            <details className="mt-2">
              <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 select-none">
                Vagy válasszon meglévő felhasználót a rendszerből →
              </summary>
              <div className="mt-3 relative user-dropdown-container">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="text"
                  placeholder="Keress felhasználó név vagy email alapján..."
                  value={userSearchTerm}
                  onChange={(e) => {
                    setUserSearchTerm(e.target.value);
                    setShowUserDropdown(true);
                  }}
                  onFocus={() => setShowUserDropdown(true)}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <ChevronDownIcon className={`h-5 w-5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${showUserDropdown ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Dropdown lista */}
              {showUserDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto">
                  {filteredAvailableUsers.length > 0 ? (
                    <div className="py-1">
                      {filteredAvailableUsers.map(usr => (
                        <div key={usr.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                          <div className="w-full text-left px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center space-x-3 transition-colors duration-150">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium text-sm">
                              {usr.displayName.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {usr.displayName}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                {usr.email}
                              </p>
                            </div>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => handleAddUser(usr, 'user')}
                                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                              >
                                User
                              </button>
                              <button
                                onClick={() => handleAddUser(usr, 'admin')}
                                className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs font-medium hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                              >
                                Admin
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {userSearchTerm ? 'Nincs találat' : 'Nincsenek elérhető felhasználók'}
                    </div>
                  )}
                </div>
              )}
            </div>
            </details>
          </div>

          {/* Felhasználók listája */}
          <div className="space-y-4">
            {/* Keresés és szűrés */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Keresés */}
                <div className="flex-1">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Keresés név vagy email alapján..."
                      value={userListSearchTerm}
                      onChange={(e) => setUserListSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Szerep szűrés */}
                <div className="flex items-center gap-2">
                  <UserGroupIcon className="h-5 w-5 text-gray-400" />
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value as typeof roleFilter)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">Összes szerep</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Táblázat */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {selectedOrganization.name} - Felhasználók ({filteredUsers.length})
                </h4>
                {selectedUserIds.size > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{selectedUserIds.size} kijelölve</span>
                    <button
                      onClick={() => handleBulkRoleChange('admin')}
                      className="px-3 py-1.5 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 transition-colors"
                    >
                      Mind Admin
                    </button>
                    <button
                      onClick={() => handleBulkRoleChange('user')}
                      className="px-3 py-1.5 bg-gray-600 text-white rounded text-xs font-medium hover:bg-gray-700 transition-colors"
                    >
                      Mind User
                    </button>
                    <button
                      onClick={handleBulkRemove}
                      className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors"
                    >
                      Eltávolítás
                    </button>
                    <button
                      onClick={() => setSelectedUserIds(new Set())}
                      className="px-3 py-1.5 text-gray-600 dark:text-gray-300 rounded text-xs font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      Mégse
                    </button>
                  </div>
                )}
              </div>

              {paginatedUsers.length === 0 ? (
                <div className="text-center py-12">
                  <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                    Nincs találat
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Próbáljon meg más keresési feltételekkel.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th scope="col" className="px-4 py-2 w-10">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.size > 0 && selectedUserIds.size === paginatedUsers.length}
                            onChange={toggleSelectAll}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Felhasználó
                        </th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Email
                        </th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Szerep
                        </th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Státusz
                        </th>
                        <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Műveletek
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {paginatedUsers.map(user => (
                        <tr key={user.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${selectedUserIds.has(user.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedUserIds.has(user.id)}
                              onChange={() => toggleUserSelection(user.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8">
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium text-sm">
                                  {user.displayName.charAt(0).toUpperCase()}
                                </div>
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {user.displayName}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              user.role === 'super_admin' 
                                ? 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 dark:from-yellow-900/30 dark:to-orange-900/30 dark:text-orange-300' :
                              user.role === 'admin' 
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : 
                              'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {user.role === 'super_admin' && '👑 '}
                              {user.role === 'super_admin' ? 'Super Admin' : user.role}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded text-xs font-medium ${
                              user.disabled === true
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            }`}>
                              {user.disabled === true ? (
                                <>
                                  <LockClosedIcon className="w-3 h-3" />
                                  <span>Inaktív</span>
                                </>
                              ) : (
                                <>
                                  <CheckCircleIcon className="w-3 h-3" />
                                  <span>Aktív</span>
                                </>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-1">
                              {user.role !== 'super_admin' && (
                                <button
                                  onClick={() => {
                                    setUserToChangeRole(user);
                                    setNewRole(user.role === 'admin' ? 'user' : 'admin');
                                  }}
                                  className="p-1.5 text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                                  title={user.role === 'admin' ? 'Visszalépés User-re' : 'Előléptetés Adminná'}
                                >
                                  <ArrowPathIcon className="w-4 h-4" />
                                </button>
                              )}
                              {user.role !== 'super_admin' ? (
                                <button
                                  onClick={() => handleManagePermissions(user)}
                                  className="p-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                                  title="Jogosultságok kezelése"
                                >
                                  <Cog6ToothIcon className="w-4 h-4" />
                                </button>
                              ) : (
                                <div className="p-1.5 text-yellow-600 dark:text-yellow-400 rounded-lg" title="Super Admin felhasználónak nincs szüksége külön jogosultságokra">
                                  <ShieldCheckIcon className="w-4 h-4" />
                                </div>
                              )}
                              {user.role !== 'super_admin' && (
                                <button
                                  onClick={() => handleToggleActive(user)}
                                  className={`p-1.5 rounded-lg transition-colors ${
                                    user.disabled === true
                                      ? 'text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                                      : 'text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                                  }`}
                                  title={user.disabled === true ? 'Aktiválás' : 'Deaktiválás'}
                                >
                                  {user.disabled === true ? <CheckCircleIcon className="w-4 h-4" /> : <LockClosedIcon className="w-4 h-4" />}
                                </button>
                              )}
                              <button
                                onClick={() => handleRemoveUser(user)}
                                className="p-1.5 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Felhasználó eltávolítása"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between bg-white dark:bg-gray-800 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Előző
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Következő
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span>
                      {' '}-{' '}
                      <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)}</span>
                      {' '}/ {' '}
                      <span className="font-medium">{filteredUsers.length}</span>
                      {' '}felhasználó
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronDownIcon className="h-5 w-5 rotate-90" />
                      </button>
                      {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-primary-50 dark:bg-primary-900/30 border-primary-500 text-primary-600 dark:text-primary-400'
                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronDownIcon className="h-5 w-5 -rotate-90" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Jogosultság szerkesztő modal */}
      {showPermissions && selectedUser && editingPermissions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-5xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-3 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm">
                    {selectedUser.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {selectedUser.displayName} - Jogosultságok
                    </h3>
                    <p className="text-xs text-purple-100">
                      {selectedUser.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPermissions(false)}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors text-white"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  <span className="font-medium text-red-600 dark:text-red-400">Nincs</span> → 
                  <span className="font-medium text-green-600 dark:text-green-400">Hozzáférés</span> → 
                  <span className="font-medium text-blue-600 dark:text-blue-400">Admin</span>
                </p>
                
                {/* Gyors beállítás gombok */}
                <div className="flex flex-wrap gap-1 p-2 bg-gray-50 dark:bg-gray-700 rounded border">
                  <button
                    onClick={() => setAllPermissions('admin')}
                    className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                  >
                    Mind Admin
                  </button>
                  <button
                    onClick={() => setAllPermissions('access')}
                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                  >
                    Mind Igen
                  </button>
                  <button
                    onClick={() => setAllPermissions('none')}
                    className="px-2 py-1 bg-gray-400 text-white rounded text-xs hover:bg-gray-500"
                  >
                    Mind Nem
                  </button>
                  <button
                    onClick={setDefaultPermissions}
                    className="px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
                  >
                    Alapért.
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {Object.entries(MODULE_CATEGORIES).map(([categoryKey, category]) => (
                  <div key={categoryKey} className="space-y-2">
                    <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide px-1">
                      {category.name}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                      {category.modules.map(moduleKey => {
                        const permission = editingPermissions[moduleKey];
                        const isNone = permission === 'none';
                        const isAccess = permission === 'access';
                        const isAdmin = permission === 'admin';
                        const moduleLabel = MODULE_LABELS[moduleKey];
                        
                        return (
                          <div key={moduleKey} className="flex items-center justify-between p-2 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                            <span className="text-sm text-gray-900 dark:text-gray-100 truncate flex-1 mr-2">{moduleLabel}</span>
                            
                            <button
                              onClick={() => togglePermission(moduleKey)}
                              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                isAdmin 
                                  ? 'bg-blue-600 text-white hover:bg-blue-700' :
                                isAccess 
                                  ? 'bg-green-600 text-white hover:bg-green-700' :
                                'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
                              }`}
                            >
                              {isAdmin ? 'Admin' : 
                               isAccess ? 'Igen' : 
                               'Nem'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Statistics */}
              <div className="mt-3 flex items-center justify-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                <span>Admin: {Object.values(editingPermissions).filter(p => p === 'admin').length}</span>
                <span>•</span>
                <span>Hozzáférés: {Object.values(editingPermissions).filter(p => p === 'access').length}</span>
                <span>•</span>
                <span>Tiltott: {Object.values(editingPermissions).filter(p => p === 'none').length}</span>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-b-xl border-t border-gray-200 dark:border-gray-600">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowPermissions(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors text-sm"
                >
                  Mégse
                </button>
                <button
                  onClick={handleSavePermissions}
                  disabled={savingPermissions}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50 text-sm"
                >
                  {savingPermissions ? '⏳ Mentés...' : '💾 Mentés'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Felhasználó törlése megerősítő modal */}
      {userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-4 py-3 rounded-t-xl">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <XMarkIcon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  Felhasználó eltávolítása
                </h3>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                  {userToDelete.displayName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">{userToDelete.displayName}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{userToDelete.email}</p>
                </div>
              </div>
              
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 dark:text-red-200">
                  <strong>Figyelem!</strong> Ez a művelet eltávolítja a felhasználót a <strong>{selectedOrganization?.name}</strong> szervezetből.
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                  A felhasználó elveszíti a hozzáférését ehhez a szervezethez.
                </p>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Biztosan el szeretnéd távolítani <strong>{userToDelete.displayName}</strong> felhasználót?
              </p>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 rounded-b-xl border-t border-gray-200 dark:border-gray-600">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setUserToDelete(null)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors text-sm"
                >
                  Mégse
                </button>
                <button
                  onClick={confirmRemoveUser}
                  className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-lg text-sm font-medium"
                >
                  🗑️ Eltávolítás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email meghívás modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    <EnvelopeIcon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Felhasználó meghívása</h3>
                </div>
                <button
                  onClick={() => { setShowInviteModal(false); setInviteEmail(''); setInviteName(''); }}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors text-white"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email cím *</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="peld@email.hu"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Ha már regisztrált, automatikusan hozzáadjuk a szervezethez. Egyébként meghívó emailt küldünk.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Név (opcionális)</label>
                <input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="Kovács János"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Szerepkör</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'admin' | 'user')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="user">Felhasználó</option>
                  <option value="admin">Adminisztrátor</option>
                </select>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 rounded-b-xl border-t border-gray-200 dark:border-gray-600">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setShowInviteModal(false); setInviteEmail(''); setInviteName(''); }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors text-sm"
                >
                  Mégse
                </button>
                <button
                  onClick={handleInviteUser}
                  disabled={!inviteEmail.trim() || inviting}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50 text-sm font-medium"
                >
                  {inviting ? '⏳ Küldés...' : '✉️ Meghívó küldése'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Szerepkör módosítás modal */}
      {userToChangeRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-3 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    <ArrowPathIcon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Szerepkör módosítása</h3>
                </div>
                <button
                  onClick={() => setUserToChangeRole(null)}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors text-white"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                  {userToChangeRole.displayName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">{userToChangeRole.displayName}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{userToChangeRole.email}</p>
                </div>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-purple-800 dark:text-purple-200">
                  Jelenlegi szerepkör: <strong>{userToChangeRole.role === 'admin' ? 'Adminisztrátor' : 'Felhasználó'}</strong>
                </p>
                <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                  Új szerepkör: <strong>{newRole === 'admin' ? 'Adminisztrátor' : 'Felhasználó'}</strong>
                </p>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => setNewRole('user')}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    newRole === 'user'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-purple-300'
                  }`}
                >
                  <UserIcon className="w-5 h-5 mx-auto mb-1" />
                  Felhasználó
                </button>
                <button
                  onClick={() => setNewRole('admin')}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    newRole === 'admin'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-purple-300'
                  }`}
                >
                  <ShieldCheckIcon className="w-5 h-5 mx-auto mb-1" />
                  Adminisztrátor
                </button>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 rounded-b-xl border-t border-gray-200 dark:border-gray-600">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setUserToChangeRole(null)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors text-sm"
                >
                  Mégse
                </button>
                <button
                  onClick={handleRoleChange}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg text-sm font-medium"
                >
                  ✓ Módosítás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleUserManager; 